/**
 * MatchesCompetitionsReport.jsx
 * Shows all match/race_day game plans.
 * Displays: date, sport, opponent/race name, result, score, duration,
 *           avg HR, performance rating, competition and round.
 */

import React, { useState, useEffect, useMemo } from 'react';
import supabase from '../supabaseClient';
import { sportLabel } from '../utils/sportLabels';
import { todayLocal, formatDateLong } from '../utils/dateLocal';

const C = {
  bg:      '#0d1117',
  card:    '#161b22',
  border:  'rgba(255,255,255,0.18)',
  text:    '#e6edf3',
  muted:   '#a8b3bd',
  logoBlue: '#38bdf8',
  accent:  '#34d399',
  win:     '#22c55e',
  loss:    '#ef4444',
  draw:    '#94a3b8',
  amber:   '#f59e0b',
  font:    "'Inter', system-ui, sans-serif",
};

// ── Range filters as logical, pre-set buckets (not free-typed min/max) ──
// Consistent with the Analytics tab's existing "Perf ≥ 3★" style filters,
// and far friendlier on mobile than a pair of number inputs. See user
// request 2026-11-02.
export const DURATION_BUCKETS = [
  { value: '', label: 'Any duration' },
  { value: '0-30', label: '< 30m', min: 0, max: 30 },
  { value: '30-60', label: '30–60m', min: 30, max: 60 },
  { value: '60-90', label: '60–90m', min: 60, max: 90 },
  { value: '90-', label: '90m+', min: 90, max: Infinity },
];
export const HR_BUCKETS = [
  { value: '', label: 'Any HR' },
  { value: '0-120', label: '< 120 bpm', min: 0, max: 120 },
  { value: '120-140', label: '120–140 bpm', min: 120, max: 140 },
  { value: '140-160', label: '140–160 bpm', min: 140, max: 160 },
  { value: '160-', label: '160+ bpm', min: 160, max: Infinity },
];
export const CALORIE_BUCKETS = [
  { value: '', label: 'Any calories' },
  { value: '0-300', label: '< 300 kcal', min: 0, max: 300 },
  { value: '300-600', label: '300–600 kcal', min: 300, max: 600 },
  { value: '600-900', label: '600–900 kcal', min: 600, max: 900 },
  { value: '900-', label: '900+ kcal', min: 900, max: Infinity },
];
const inBucket = (buckets, bucketValue, val) => {
  if (!bucketValue) return true;
  const b = buckets.find(x => x.value === bucketValue);
  if (!b || val == null || val === '') return false;
  const n = Number(val);
  return n >= b.min && n < b.max;
};

function fmtDate(iso) {
  if (!iso) return '—';
  return formatDateLong(iso);
}

function fmtScore(matchScore, sport) {
  if (!matchScore || Object.keys(matchScore).length === 0) return null;
  if (matchScore.sets) {
    const sets = matchScore.sets.filter(s => s.me !== '' && s.opp !== '');
    if (!sets.length) return null;
    return sets.map(s => {
      // Standard tennis notation shows only the tiebreak loser's points,
      // e.g. 7-6(4) — not the full tiebreak score.
      const tb = s.tb && s.tb.me !== '' && s.tb.opp !== ''
        ? `(${Math.min(Number(s.tb.me), Number(s.tb.opp))})` : '';
      return `${s.me}-${s.opp}${tb}`;
    }).join(', ');
  }
  if (matchScore.me !== undefined) return `${matchScore.me} — ${matchScore.opp}`;
  return null;
}

function ResultBadge({ result, scheduledDate }) {
  if (!result) {
    // No result logged yet — tell the two different situations apart
    // instead of a bare "—": a past/today session is waiting on its
    // result, a future one just hasn't happened yet. See user feedback
    // 2026-10-02.
    if (!scheduledDate) return <span style={{ color: C.muted, fontSize: 12 }}>—</span>;
    const isFuture = scheduledDate.slice(0, 10) > todayLocal();
    const cfg = isFuture
      ? { label: 'Scheduled', color: '#8b5cf6' }
      : { label: 'Waiting for result', color: C.amber };
    return (
      <span style={{
        fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
        background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}40`,
        whiteSpace: 'nowrap',
      }}>{cfg.label}</span>
    );
  }
  const cfg = {
    win:          { label: 'Win',          color: C.win  },
    loss:         { label: 'Loss',         color: C.loss },
    draw:         { label: 'Draw',         color: C.draw },
    achieved:     { label: 'Achieved',     color: C.win  },
    not_achieved: { label: 'Not achieved', color: C.amber },
  }[result] || { label: result, color: C.muted };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
      background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}40`,
    }}>{cfg.label}</span>
  );
}

function Stars({ value }) {
  if (!value) return <span style={{ color: C.muted, fontSize: 12 }}>—</span>;
  return (
    <span style={{ fontSize: 13, letterSpacing: 1 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} style={{ color: n <= value ? '#f59e0b' : C.border }}>★</span>
      ))}
    </span>
  );
}

function sportEmoji(sport) {
  const map = {
    tennis: '🎾', padel: '🎾', squash: '🎾', badminton: '🏸',
    football: '⚽', soccer: '⚽', basketball: '🏀', volleyball: '🏐',
    running: '🏃', cycling: '🚴', swimming: '🏊', triathlon: '🏅',
    golf: '⛳', bowling: '🎳', hockey: '🏒', rugby: '🏉',
    boxing: '🥊', wrestling: '🤼', judo: '🥋', karate: '🥋',
  };
  return map[(sport || '').toLowerCase()] || '🏆';
}

export default function MatchesCompetitionsReport({ userId, activities, activeSport, filterSport = '', filterResult = '', filterOpponent = '', filterCompetition = '', filterCompetitionId = '', filterFormat = '', filterDuration = '', filterHr = '', filterCalories = '', competitionOnly = false, competitions = [], onOpenDetail, demoMode = false, demoPlans = [], initialSortAsc = false, bare = false, onCount }) {
  const compById = useMemo(() => {
    const m = {};
    competitions.forEach(c => { m[c.id] = c; });
    return m;
  }, [competitions]);
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState(initialSortAsc ? 'asc' : 'desc');

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  useEffect(() => {
    // Demo mode has no authenticated Supabase session, so a direct RLS
    // query (below) always returns zero rows for a userId that isn't
    // auth.uid() — the table silently stayed empty. In demo mode, use the
    // plans the parent already fetched from the public /demo endpoint
    // instead of querying Supabase directly.
    if (demoMode) {
      setPlans((demoPlans || []).filter(p => ['match', 'competition', 'race_day'].includes(p.type)));
      setLoading(false);
      return;
    }
    if (!userId) return;
    // Fetch match/race_day plans for the active sport only — full isolation
    // between sports (2026-10-18, PRODUCT-GUIDELINES.md §5). We'll resolve
    // activity via linked_id OR same-day auto-match.
    let q = supabase
      .from('session_plans')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .in('type', ['match', 'competition', 'race_day']);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('scheduled_date', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('MatchesCompetitionsReport fetch error:', error);
        setPlans(data || []);
        setLoading(false);
      })
      .catch(e => {
        console.error('MatchesCompetitionsReport error:', e);
        setLoading(false);
      });
  }, [userId, demoMode, demoPlans, activeSport]);

  // Duration, avg HR and calories are the player's own optional entries on the plan
  const resolveAct = (plan) => ({
    duration_minutes: plan.actual_duration_minutes ?? null,
    avg_hr: plan.avg_heart_rate ?? null,
    calories: plan.calories ?? null,
  });

  // Apply external filters
  const filteredPlans = useMemo(() => plans.filter(p => {
    if (competitionOnly && !p.competition_id) return false;
    if (filterSport    && p.sport !== filterSport) return false;
    if (filterResult   && p.result !== filterResult) return false;
    if (filterOpponent && p.opponent_name !== filterOpponent) return false;
    if (filterCompetitionId && p.competition_id !== filterCompetitionId) return false;
    if (filterCompetition) {
      const name = compById[p.competition_id]?.name || '';
      if (!name.toLowerCase().includes(filterCompetition.toLowerCase())) return false;
    }
    if (filterFormat && (p.match_format || 'singles') !== filterFormat) return false;
    if (!inBucket(DURATION_BUCKETS, filterDuration, p.actual_duration_minutes)) return false;
    if (!inBucket(HR_BUCKETS, filterHr, p.avg_heart_rate)) return false;
    if (!inBucket(CALORIE_BUCKETS, filterCalories, p.calories)) return false;
    return true;
  }), [plans, competitionOnly, filterSport, filterResult, filterOpponent, filterCompetitionId, filterCompetition, filterFormat, filterDuration, filterHr, filterCalories, compById]);

  // Lets the parent (FormSituationPanel) show the live event count in its
  // own top-level header when this component is embedded `bare` — see the
  // cardStyle comment below for why the header itself moved up a level.
  useEffect(() => {
    if (onCount) onCount(filteredPlans.length);
  }, [filteredPlans.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (!filteredPlans.length) return [];
    const withAct = filteredPlans.map(plan => ({ plan, act: resolveAct(plan) }));
    const val = ({ plan, act }) => {
      if (sortKey === 'date')     return plan.scheduled_date || '';
      if (sortKey === 'result')   return plan.result || '';
      if (sortKey === 'duration') return act?.duration_minutes || 0;
      if (sortKey === 'hr')       return act?.avg_hr || 0;
      if (sortKey === 'calories') return act?.calories || 0;
      if (sortKey === 'perf')     return plan.performance_score || 0;
      return '';
    };
    return [...withAct].sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [filteredPlans, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMobile = window.innerWidth < 640;

  if (loading) return null;

  // `bare`: when embedded inside another bordered card (FormSituationPanel),
  // skip this component's own card chrome — two nested blue borders around
  // one table read as a mistake, not a design, per user feedback
  // 2026-10-02. The header/count stay here either way since the count
  // depends on this component's own filtering logic.
  const cardStyle = bare ? { marginBottom: 0 } : {
    background: 'rgba(15,23,42,0.82)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: '1.5px solid rgba(56,189,248,0.65)',
    borderRadius: 14, padding: '20px 20px 8px', marginBottom: 20,
  };

  if (!filteredPlans.length) return (
    <div style={cardStyle}>
      {!bare && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: C.logoBlue, fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>
            🏆 MATCHES & COMPETITIONS
          </h3>
          <span style={{ fontSize: 12, color: C.muted }}>0 events</span>
        </div>
      )}
      <div style={{
        padding: '32px 20px', textAlign: 'center',
        border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 10, marginBottom: 12,
      }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>🏆</div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
          No matches or competitions yet
        </div>
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, maxWidth: 340, margin: '0 auto' }}>
          Add <strong style={{ color: C.text }}>Match</strong> or <strong style={{ color: C.text }}>Race Day</strong> sessions
          in your Game Plan and log the result — they will appear here with score and performance data.
        </div>
      </div>
    </div>
  );

  return (
    <div style={cardStyle}>
      {/* Header */}
      {!bare && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: C.logoBlue, fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>
            🏆 MATCHES & COMPETITIONS
          </h3>
          <span style={{ fontSize: 12, color: C.muted }}>{filteredPlans.length} events</span>
        </div>
      )}

      {/* Mobile card list */}
      {isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(({ plan, act, trainingBetween }, i) => {
            const score = fmtScore(plan.match_score, plan.sport);
            const name  = plan.opponent_name || plan.race_name || null;
            return (
              <div key={plan.id}
                onClick={onOpenDetail ? () => onOpenDetail(plan) : undefined}
                style={{
                background: i === 0 ? 'rgba(52,211,153,0.06)' : 'rgba(255,255,255,0.03)',
                // Thin, low-opacity blue — a deliberately lighter touch than
                // the bold module-level border, so every row gets a bit of
                // the same accent without it reading as "too much" blue
                // everywhere. See user feedback 2026-10-02.
                border: `1px solid ${i === 0 ? 'rgba(52,211,153,0.25)' : 'rgba(56,189,248,0.30)'}`,
                borderRadius: 10, padding: '12px 14px',
                cursor: onOpenDetail ? 'pointer' : 'default',
              }}>
                {/* Top row: emoji + sport + date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 20 }}>{sportEmoji(plan.sport)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                      {sportLabel(plan.sport)}
                      {name && <span style={{ color: C.muted, fontWeight: 400, fontSize: 12, marginLeft: 6 }}>vs {name}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{fmtDate(plan.scheduled_date?.slice(0, 10))}</div>
                  </div>
                  <ResultBadge result={plan.result} scheduledDate={plan.scheduled_date} />
                </div>
                {/* Competition / round — only if present */}
                {(compById[plan.competition_id]?.name || plan.round_label) && (
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                    🏆 {compById[plan.competition_id]?.name}{plan.round_label ? ` · ${plan.round_label}` : ''}
                  </div>
                )}
                {/* Score + perf row */}
                {(score || plan.performance_score) && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
                    {score && <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{score}</span>}
                    {plan.performance_score && <Stars value={plan.performance_score} />}
                  </div>
                )}
                {/* Stats row */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
                  {act?.duration_minutes && <span style={{ color: C.muted }}>⏱ {act.duration_minutes}m</span>}
                  {act?.avg_hr && <span style={{ color: C.muted }}>❤️ {Math.round(act.avg_hr)} bpm</span>}
                  {act?.calories && <span style={{ color: C.muted }}>🔥 {Math.round(act.calories)} kcal</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: C.text }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {[
                  { label: 'Date',        key: 'date'     },
                  { label: 'Event',       key: null        },
                  { label: 'Result',      key: 'result'   },
                  { label: 'Score',       key: null        },
                  { label: 'Duration',    key: 'duration' },
                  { label: 'HR',          key: 'hr'       },
                  { label: 'Cal',         key: 'calories' },
                  { label: 'Perf.',       key: 'perf'     },
                  { label: 'Competition', key: null        },
                  { label: 'Round',       key: null        },
                ].map(({ label, key }) => (
                  <th key={label}
                    onClick={key ? () => toggleSort(key) : undefined}
                    style={{
                      padding: '6px 10px 10px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700,
                      color: key && sortKey === key ? C.accent : C.muted,
                      textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
                      cursor: key ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}>{label}{key ? sortArrow(key) : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ plan, act, trainingBetween }, i) => {
                const isFirst = i === 0;
                const score = fmtScore(plan.match_score, plan.sport);
                const name = plan.opponent_name || plan.race_name || null;

                return (
                  <tr key={plan.id}
                    style={{
                      borderBottom: `1px solid rgba(56,189,248,0.30)`,
                      background: isFirst ? 'rgba(52,211,153,0.04)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '12px 10px', whiteSpace: 'nowrap', color: C.muted, fontSize: 12 }}>
                      {fmtDate(plan.scheduled_date?.slice(0, 10))}
                    </td>
                    <td style={{ padding: '12px 10px', minWidth: 140 }}>
                      <div
                        onClick={onOpenDetail ? () => onOpenDetail(plan) : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: onOpenDetail ? 'pointer' : 'default' }}
                      >
                        <span>{sportEmoji(plan.sport)}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: onOpenDetail ? C.accent : C.text, textDecoration: onOpenDetail ? 'underline' : 'none', textDecorationColor: 'rgba(52,211,153,0.35)' }}>
                            {sportLabel(plan.sport)}
                          </div>
                          {name && <div style={{ fontSize: 11, color: C.muted }}>{name}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 10px' }}><ResultBadge result={plan.result} scheduledDate={plan.scheduled_date} /></td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>
                      {score || <span style={{ color: C.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>
                      {act?.duration_minutes ? `${act.duration_minutes}m` : '—'}
                    </td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.text }}>{act?.avg_hr || '—'}</td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.text }}>{act?.calories || '—'}</td>
                    <td style={{ padding: '12px 10px' }}><Stars value={plan.performance_score} /></td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>
                      {compById[plan.competition_id]?.name || <span style={{ color: C.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 10px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
                      {plan.round_label || <span style={{ color: C.muted }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
